import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { OtaService } from 'src/app/Core/services/ota.service';
import { ToasterService } from 'src/app/Core/services/toaster.service';

interface DropdownOption {
  label: string;
  value: string;
}

interface GeneratedKeyRecord {
  model: string;
  keyAlgorithm: string;
  keyLength: number;
  publicKeys: { [keyId: string]: string };
  createdAt: Date;
  createdBy: string;
}

interface KeyCreationForm {
  signingModel: string;
  signingAlgorithm: string;
  numberOfKeys: number;
}

@Component({
  selector: 'app-ota-device-key',
  templateUrl: './ota-device-key.component.html',
  styleUrls: ['./ota-device-key.component.css']
})
export class OtaDeviceKeyComponent {
  readonly itemsPerPage = 5;
  currentPage = 1;
  copiedKeyId: string | null = null;
  popupKeys: { id: string; value: string }[] | null = null;
  searchQuery = '';
  selectedFilterValue = 'model';
  filteredGeneratedKeys: GeneratedKeyRecord[] = [];

  readonly filterOptions: DropdownOption[] = [
    { label: 'Model', value: 'model' }
  ];

  readonly signingAlgorithmOptions: DropdownOption[] = [
    { label: 'RSA_3072', value: 'RSA_3072' },
    { label: 'RSA_2048', value: 'RSA_2048' },
    { label: 'RSA_4096', value: 'RSA_4096' },
    { label: 'ECC_NIST_P256', value: 'ECC_NIST_P256' },
    { label: 'ECC_NIST_P384', value: 'ECC_NIST_P384' },
    { label: 'ECC_NIST_P521', value: 'ECC_NIST_P521' },
    { label: 'ECC_SECG_P256K1', value: 'ECC_SECG_P256K1' },
    { label: 'ECC_NIST_EDWARDS25519', value: 'ECC_NIST_EDWARDS25519' },
  ];

  readonly numberOfKeysOptions: DropdownOption[] = [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' }
  ];

  formData: KeyCreationForm = {
    signingModel: '',
    signingAlgorithm: 'RSA_3072',
    numberOfKeys: 1
  };

  generatedKeys: GeneratedKeyRecord[] = [];

  constructor(private otaService: OtaService, private toaster: ToasterService,) {}

  ngOnInit(): void {
    this.fetchDeviceKmsList();
  }

  fetchDeviceKmsList() {
    this.otaService.getDeviceKms().subscribe({
      next: (data: any) => {
        console.log("Device KMS List:", data);
        this.generatedKeys = data.map((item: any) => ({
          model: item.model,
          keyAlgorithm: item.keyAlgorithm,
          keyLength: item.keyLength,
          createdBy: item.createdBy,
          createdAt: new Date(item.createdAt),
          publicKeys: item.publicKeys
        })).sort((a: GeneratedKeyRecord, b: GeneratedKeyRecord) => b.createdAt.getTime() - a.createdAt.getTime());
        this.currentPage = 1;
        this.applyFilters();
      },
      error: (error: any) => {
        console.error('Error fetching device KMS list:', error);
        if (error instanceof HttpErrorResponse) {
          this.toaster.openDialogBox(error.error.message, false);
        }
      }
    });
  }

  getPublicKeyEntries(publicKeys: { [keyId: string]: string }): { id: string; value: string }[] {
    return Object.entries(publicKeys).map(([id, value]) => ({ id, value }));
  }

  truncateKey(keyValue: string): string {
    const lines = keyValue.trim().split('\n');
    if (lines.length <= 2) return keyValue;
    const preview = lines[0] + '\n' + lines[1].substring(0, 40);
    return preview + '...';
  }

  openKeyPopup(publicKeys: { [keyId: string]: string }): void {
    this.popupKeys = this.getPublicKeyEntries(publicKeys);
  }

  closeKeyPopup(): void {
    this.popupKeys = null;
    this.copiedKeyId = null;
  }

  copyToClipboard(entryId: string, keyValue: string): void {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(keyValue).then(() => {
        this.copiedKeyId = entryId;
        setTimeout(() => { this.copiedKeyId = null; }, 2000);
      });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = keyValue;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.copiedKeyId = entryId;
      setTimeout(() => { this.copiedKeyId = null; }, 2000);
    }
  }

  createKeys(): void {
    const keyCount = Number(this.formData.numberOfKeys);

    this.otaService.createDeviceKmsKey(this.formData.signingModel, keyCount, this.formData.signingAlgorithm).subscribe({
      next: (response: any) => {
        console.log('Device KMS key created successfully:', response);
        this.toaster.openDialogBox('Device KMS key created successfully', false);
        this.formData = {
          signingModel: '',
          signingAlgorithm: 'RSA_3072',
          numberOfKeys: 1
        };
        this.fetchDeviceKmsList();
      },
      error: (error: any) => {
        console.error('Error creating device KMS key:', error);
        if (error instanceof HttpErrorResponse) {
          this.toaster.openDialogBox(error.error.message, false);
        }
      }
    });
  }

  trackByCreatedAt(index: number, record: GeneratedKeyRecord): string {
    return `${record.createdBy}-${record.createdAt.toISOString()}-${index}`;
  }

  get paginatedGeneratedKeys(): GeneratedKeyRecord[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;

    return this.filteredGeneratedKeys.slice(startIndex, endIndex);
  }

  applyFilters(): void {
    let filtered: GeneratedKeyRecord[] = [...this.generatedKeys];

    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter((record) => record.model.toLowerCase().includes(query));
    }

    this.filteredGeneratedKeys = filtered;

    if (this.currentPage > this.totalPages && this.totalPages > 0) {
      this.currentPage = 1;
    }
  }

  onSearch(): void {
    this.currentPage = 1;
    this.applyFilters();
  }

  onFilterSelectionChange(value: string): void {
    this.selectedFilterValue = value;
    this.currentPage = 1;
    this.applyFilters();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  get totalRecords(): number {
    return this.filteredGeneratedKeys.length;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRecords / this.itemsPerPage));
  }

  get startItem(): number {
    if (this.totalRecords === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get endItem(): number {
    if (this.totalRecords === 0) {
      return 0;
    }

    return Math.min(this.currentPage * this.itemsPerPage, this.totalRecords);
  }
}
